import { useEffect, useMemo, useState } from 'react';
import {
  buildAutoUploadFilterOptions,
  loadAutoUploadFilterAvailability,
} from './autoUploadFilterOptions';

/**
 * Returns the "EMR upload status" filter options this org should see.
 *
 * Availability starts null (unknown), which builds the full list, so the filter never
 * renders short while the request is in flight.
 *
 * @param {Object}  params
 * @param {boolean} params.createPatientChart  `create_patient_chart` feature flag
 * @param {boolean} params.taskCreation        `task_creation` feature flag
 * @param {boolean} params.hideTaskButton      `hide_task_creation_button` feature flag
 * @param {Array}   params.selectedValues      currently applied filters, never hidden
 * @param {boolean} params.enabled             skip the request when the filter is hidden
 */
const useAutoUploadFilterOptions = ({
  createPatientChart = false,
  taskCreation = false,
  hideTaskButton = false,
  selectedValues = [],
  enabled = true,
} = {}) => {
  const [availability, setAvailability] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;

    let isMounted = true;
    loadAutoUploadFilterAvailability().then((result) => {
      if (isMounted) setAvailability(result);
    });

    return () => {
      isMounted = false;
    };
  }, [enabled]);

  // selectedValues is a fresh array on every render, so memoize on its contents.
  const selectedKey = selectedValues.join(',');

  return useMemo(
    () => buildAutoUploadFilterOptions({
      createPatientChart,
      taskCreation,
      hideTaskButton,
      availability,
      selectedValues,
    }),
    [createPatientChart, taskCreation, hideTaskButton, availability, selectedKey],
  );
};

export default useAutoUploadFilterOptions;
